/**
 * TypeScript wrapper around the vendored libopus WebAssembly build.
 *
 * The build at `vendor/libopus.js` is an Emscripten ES module exporting a
 * factory `createOpusModule()` that resolves to a `Module` object holding
 * the raw `_opus_*` C entry points and `_malloc`/`_free`. The wrappers in
 * this file hide the manual heap-pointer juggling behind two ordinary
 * classes — [`OpusEncoder`] and [`OpusDecoder`] — and centralise the
 * configuration choices the project has standardised on:
 *
 *  - **16 kHz** mono sample rate
 *  - **20 ms** frame duration (320 samples per channel)
 *  - **VOIP** application profile (DTX off, FEC on, voice signal type)
 *  - **~24 kbps** target bitrate (CBR; tight enough for ESP32 uplink,
 *    high enough to stay intelligible through transient packet loss)
 *
 * The module is loaded once, lazily, on the first call to [`loadOpus`];
 * subsequent calls share the same `Module` instance. Both encoder and
 * decoder hold their own state pointer and *must* be `destroy()`ed to
 * release the WASM heap allocations they own.
 */

import createOpusModule from "../../vendor/libopus.js";

/** Sample rate the project standardises on for both encode and decode. */
export const OPUS_SAMPLE_RATE = 16_000;

/** Channel count — mono everywhere. */
export const OPUS_CHANNELS = 1;

/** Frame duration in milliseconds. Fixed by `PROTOCOL.md`. */
export const OPUS_FRAME_DURATION_MS = 20;

/** Samples per channel per encoded frame: 320 at 16 kHz × 20 ms. */
export const OPUS_FRAME_SAMPLES = (OPUS_SAMPLE_RATE * OPUS_FRAME_DURATION_MS) / 1000;

/**
 * Maximum bytes a single encoded packet can occupy.
 *
 * Opus's theoretical maximum is 1275 bytes; we round up to 1500 to give
 * the encoder unconditional headroom and to leave space for any future
 * configuration change without revisiting the buffer size.
 */
export const OPUS_MAX_PACKET_BYTES = 1500;

/** Target encode bitrate in bits per second. */
export const OPUS_DEFAULT_BITRATE = 24_000;

// ─── Opus C constants (mirrored from `opus_defines.h`) ────────────────

/** `opus_application_t` — voice over IP (favours intelligibility). */
const OPUS_APPLICATION_VOIP = 2048;

/** `opus_signal_t` — voice signal type. */
const OPUS_SIGNAL_VOICE = 3001;

// ─── Encoder CTL request codes ────────────────────────────────────────
const OPUS_SET_BITRATE_REQUEST = 4002;
const OPUS_SET_VBR_REQUEST = 4006;
const OPUS_SET_COMPLEXITY_REQUEST = 4010;
const OPUS_SET_INBAND_FEC_REQUEST = 4012;
const OPUS_SET_PACKET_LOSS_PERC_REQUEST = 4014;
const OPUS_SET_DTX_REQUEST = 4016;
const OPUS_SET_SIGNAL_REQUEST = 4024;

/**
 * Shape of the Emscripten-generated module after the factory promise
 * resolves. Only the exports actually used by this file are listed; the
 * underlying object also contains the runtime utilities Emscripten
 * generates (`HEAP*`, `getValue`, `setValue`, …) which are accessed via
 * dedicated typed properties below.
 */
interface OpusModule {
    _malloc(size: number): number;
    _free(ptr: number): void;

    _opus_strerror(code: number): number;

    _opus_encoder_create(fs: number, channels: number, application: number, errorPtr: number): number;
    _opus_encoder_destroy(encoder: number): void;
    /**
     * Shim around libopus's variadic `opus_encoder_ctl`. The variadic
     * form cannot be called reliably from JS — see `build-opus.sh`.
     */
    _nettacco_opus_encoder_ctl_set_int(encoder: number, request: number, value: number): number;
    _opus_encode(encoder: number, pcm: number, frameSize: number, data: number, maxDataBytes: number): number;

    _opus_decoder_create(fs: number, channels: number, errorPtr: number): number;
    _opus_decoder_destroy(decoder: number): void;
    /** Shim around the variadic `opus_decoder_ctl`. */
    _nettacco_opus_decoder_ctl_set_int(decoder: number, request: number, value: number): number;
    _opus_decode(decoder: number, data: number, len: number, pcm: number, frameSize: number, decodeFec: number): number;

    HEAP8: Int8Array;
    HEAPU8: Uint8Array;
    HEAP16: Int16Array;
    HEAP32: Int32Array;
    HEAPU32: Uint32Array;
    getValue(ptr: number, type: string): number;
    setValue(ptr: number, value: number, type: string): void;
}

/**
 * Lazily-resolved promise for the loaded module. The Emscripten factory
 * is itself idempotent only in the sense that re-calling it spins up
 * another instance; we deliberately memoise the *first* call so encoder
 * and decoder share a single WASM heap.
 */
let modulePromise: Promise<OpusModule> | null = null;

/**
 * Load (or return the already-loaded) libopus WASM module.
 *
 * Resolves to a module on which `_malloc`, `_free`, `_opus_*`, and the
 * `HEAP*` typed-array views are available. Subsequent calls return the
 * same instance.
 *
 * Throws if the WASM binary fails to fetch or instantiate — the caller
 * will typically surface this as a UI-level "audio unavailable" error.
 */
export async function loadOpus(): Promise<OpusModule> {
    if (modulePromise === null) {
        modulePromise = createOpusModule() as Promise<OpusModule>;
    }
    return modulePromise;
}

/**
 * Read a NUL-terminated UTF-8 string out of the module's heap.
 *
 * Used to pretty-print Opus error codes via `opus_strerror`. We do not
 * use Emscripten's bundled `UTF8ToString` because the build does not
 * export it; this is a tiny inlined equivalent.
 */
function readCString(mod: OpusModule, ptr: number): string {
    if (ptr === 0) return "";
    const heap = mod.HEAPU8;
    let end = ptr;
    while (heap[end] !== 0) end++;
    return new TextDecoder().decode(heap.subarray(ptr, end));
}

/**
 * Translate an Opus error code (negative integer) to a human-readable
 * `Error`. Used by both encoder and decoder.
 */
function opusError(mod: OpusModule, code: number, context: string): Error {
    const msg = readCString(mod, mod._opus_strerror(code));
    return new Error(`${context}: ${msg} (code ${code})`);
}

// ─── Encoder ──────────────────────────────────────────────────────────

/** Constructor options for [`OpusEncoder`]. */
export interface OpusEncoderOptions {
    /** Target bitrate in bits per second. Defaults to {@link OPUS_DEFAULT_BITRATE}. */
    bitrate?: number;
    /**
     * Encode complexity (0–10). Higher = better quality, more CPU. We
     * default to 5, which is a good speech/CPU balance on browsers and
     * still fits comfortably on ESP32-S3 if this same wrapper were
     * ported.
     */
    complexity?: number;
    /**
     * Hint to the encoder about expected packet loss percentage. Only
     * meaningful when in-band FEC is on (which it is, by default).
     */
    expectedPacketLossPercent?: number;
}

/**
 * Streaming Opus encoder configured for 16 kHz mono VOIP.
 *
 * Hold one of these per active capture session. Call [`encode`] with a
 * 20 ms frame of 16-bit PCM samples; it returns a freshly-allocated
 * `Uint8Array` containing the compressed packet. Call [`destroy`] when
 * done to free the underlying WASM allocations.
 */
export class OpusEncoder {
    /** WASM module shared with all other Opus users in this page. */
    private readonly mod: OpusModule;
    /** Pointer to the `OpusEncoder` C struct. */
    private encoderPtr: number;
    /** Heap buffer for the input PCM samples (one frame, int16). */
    private readonly pcmPtr: number;
    /** Heap buffer for the output compressed packet. */
    private readonly outPtr: number;

    /**
     * Create and configure an encoder. The constructor is private — use
     * the async [`OpusEncoder.create`] factory which loads the module.
     */
    private constructor(mod: OpusModule, encoderPtr: number, pcmPtr: number, outPtr: number) {
        this.mod = mod;
        this.encoderPtr = encoderPtr;
        this.pcmPtr = pcmPtr;
        this.outPtr = outPtr;
    }

    /**
     * Construct an encoder, loading the WASM module if necessary.
     *
     * Configures CBR at the requested bitrate, voice signal type, FEC
     * on, DTX off (so the transport sees a steady stream of frames and
     * no PLC heuristics need to fire on the receive side).
     */
    public static async create(opts: OpusEncoderOptions = {}): Promise<OpusEncoder> {
        const mod = await loadOpus();
        const bitrate = opts.bitrate ?? OPUS_DEFAULT_BITRATE;
        const complexity = opts.complexity ?? 5;
        const lossPct = opts.expectedPacketLossPercent ?? 5;

        const errPtr = mod._malloc(4);
        const encoderPtr = mod._opus_encoder_create(
            OPUS_SAMPLE_RATE,
            OPUS_CHANNELS,
            OPUS_APPLICATION_VOIP,
            errPtr,
        );
        const errCode = mod.getValue(errPtr, "i32");
        mod._free(errPtr);
        if (errCode !== 0 || encoderPtr === 0) {
            throw opusError(mod, errCode, "opus_encoder_create");
        }

        // Apply CTLs via the non-variadic shim. Failures here are
        // programmer errors (bad request codes), not runtime conditions,
        // so they bubble up as `Error`.
        const setInt = (request: number, value: number): number =>
            mod._nettacco_opus_encoder_ctl_set_int(encoderPtr, request, value);
        ctlOk(mod, "OPUS_SET_SIGNAL", setInt(OPUS_SET_SIGNAL_REQUEST, OPUS_SIGNAL_VOICE));
        ctlOk(mod, "OPUS_SET_BITRATE", setInt(OPUS_SET_BITRATE_REQUEST, bitrate));
        ctlOk(mod, "OPUS_SET_VBR", setInt(OPUS_SET_VBR_REQUEST, 0));
        ctlOk(mod, "OPUS_SET_COMPLEXITY", setInt(OPUS_SET_COMPLEXITY_REQUEST, complexity));
        ctlOk(mod, "OPUS_SET_INBAND_FEC", setInt(OPUS_SET_INBAND_FEC_REQUEST, 1));
        ctlOk(mod, "OPUS_SET_PACKET_LOSS_PERC", setInt(OPUS_SET_PACKET_LOSS_PERC_REQUEST, lossPct));
        ctlOk(mod, "OPUS_SET_DTX", setInt(OPUS_SET_DTX_REQUEST, 0));

        // Reusable scratch buffers — allocated once, sized for the
        // worst-case frame and packet.
        const pcmPtr = mod._malloc(OPUS_FRAME_SAMPLES * OPUS_CHANNELS * 2);
        const outPtr = mod._malloc(OPUS_MAX_PACKET_BYTES);
        return new OpusEncoder(mod, encoderPtr, pcmPtr, outPtr);
    }

    /**
     * Encode a single 20 ms frame of PCM into a compressed packet.
     *
     * `pcm` must contain exactly {@link OPUS_FRAME_SAMPLES} 16-bit
     * signed samples (320 for 16 kHz mono). The returned `Uint8Array`
     * is a fresh copy owned by the caller — it is safe to retain past
     * the next call to `encode`.
     */
    public encode(pcm: Int16Array): Uint8Array {
        if (pcm.length !== OPUS_FRAME_SAMPLES) {
            throw new Error(`opus encode: expected ${OPUS_FRAME_SAMPLES} samples, got ${pcm.length}`);
        }
        if (this.encoderPtr === 0) throw new Error("opus encode: encoder destroyed");

        // Copy samples into the WASM heap. `set` on an Int16Array view
        // honours the source's byte representation — exactly what we
        // want for `opus_int16` interleaved PCM.
        this.mod.HEAP16.set(pcm, this.pcmPtr >> 1);

        const written = this.mod._opus_encode(
            this.encoderPtr,
            this.pcmPtr,
            OPUS_FRAME_SAMPLES,
            this.outPtr,
            OPUS_MAX_PACKET_BYTES,
        );
        if (written < 0) throw opusError(this.mod, written, "opus_encode");

        // `slice` returns a fresh ArrayBuffer-backed copy, decoupled
        // from the WASM heap so a future grow / encode cannot disturb
        // it.
        return this.mod.HEAPU8.slice(this.outPtr, this.outPtr + written);
    }

    /**
     * Release all WASM-side resources owned by this encoder. Idempotent
     * — calling twice is harmless.
     */
    public destroy(): void {
        if (this.encoderPtr !== 0) {
            this.mod._opus_encoder_destroy(this.encoderPtr);
            this.encoderPtr = 0;
        }
        if (this.pcmPtr !== 0) this.mod._free(this.pcmPtr);
        if (this.outPtr !== 0) this.mod._free(this.outPtr);
    }
}

// ─── Decoder ──────────────────────────────────────────────────────────

/**
 * Streaming Opus decoder configured for 16 kHz mono.
 *
 * Hold one of these per remote talker (keyed by `connection_id`) so
 * the decoder's internal state — packet history used for PLC and FEC —
 * remains consistent. Call [`decode`] with the compressed bytes from
 * an audio frame; it returns the resulting PCM samples. Call
 * [`destroy`] when the talker leaves to free the WASM allocations.
 */
export class OpusDecoder {
    private readonly mod: OpusModule;
    private decoderPtr: number;
    /** Heap buffer for the input compressed packet. */
    private readonly inPtr: number;
    /** Heap buffer for the output PCM samples (worst case: max frame). */
    private readonly pcmPtr: number;
    /**
     * Largest single decoded frame Opus may produce given our sample
     * rate. The spec allows up to 120 ms per packet; we size for that
     * worst case so a misbehaving sender cannot overrun the buffer.
     */
    private static readonly MAX_DECODED_SAMPLES = (OPUS_SAMPLE_RATE * 120) / 1000;

    private constructor(mod: OpusModule, decoderPtr: number, inPtr: number, pcmPtr: number) {
        this.mod = mod;
        this.decoderPtr = decoderPtr;
        this.inPtr = inPtr;
        this.pcmPtr = pcmPtr;
    }

    /** Construct a decoder, loading the WASM module if necessary. */
    public static async create(): Promise<OpusDecoder> {
        const mod = await loadOpus();
        const errPtr = mod._malloc(4);
        const decoderPtr = mod._opus_decoder_create(OPUS_SAMPLE_RATE, OPUS_CHANNELS, errPtr);
        const errCode = mod.getValue(errPtr, "i32");
        mod._free(errPtr);
        if (errCode !== 0 || decoderPtr === 0) {
            throw opusError(mod, errCode, "opus_decoder_create");
        }

        const inPtr = mod._malloc(OPUS_MAX_PACKET_BYTES);
        const pcmPtr = mod._malloc(OpusDecoder.MAX_DECODED_SAMPLES * OPUS_CHANNELS * 2);
        return new OpusDecoder(mod, decoderPtr, inPtr, pcmPtr);
    }

    /**
     * Decode one compressed packet to PCM samples.
     *
     * The returned `Int16Array` is a fresh copy whose length is the
     * actual sample count produced by the decoder (the protocol
     * guarantees 20 ms frames, so in practice this is always
     * {@link OPUS_FRAME_SAMPLES}, but we honour what Opus reports).
     *
     * Throws on malformed packets — callers should treat this as
     * "drop this frame" rather than "tear down the decoder".
     */
    public decode(packet: Uint8Array): Int16Array {
        if (this.decoderPtr === 0) throw new Error("opus decode: decoder destroyed");
        if (packet.length === 0 || packet.length > OPUS_MAX_PACKET_BYTES) {
            throw new Error(`opus decode: invalid packet length ${packet.length}`);
        }

        this.mod.HEAPU8.set(packet, this.inPtr);
        const samples = this.mod._opus_decode(
            this.decoderPtr,
            this.inPtr,
            packet.length,
            this.pcmPtr,
            OpusDecoder.MAX_DECODED_SAMPLES,
            0,
        );
        if (samples < 0) throw opusError(this.mod, samples, "opus_decode");

        const start = this.pcmPtr >> 1;
        return this.mod.HEAP16.slice(start, start + samples * OPUS_CHANNELS);
    }

    /**
     * Release all WASM-side resources owned by this decoder. Idempotent.
     */
    public destroy(): void {
        if (this.decoderPtr !== 0) {
            this.mod._opus_decoder_destroy(this.decoderPtr);
            this.decoderPtr = 0;
        }
        if (this.inPtr !== 0) this.mod._free(this.inPtr);
        if (this.pcmPtr !== 0) this.mod._free(this.pcmPtr);
    }
}

/**
 * Throw if a CTL call returned an error.
 *
 * `opus_*_ctl` returns 0 on success and a negative `OPUS_*` code on
 * failure. We treat any failure as a programming error here, since the
 * CTL requests we issue are well-defined and supported by all Opus
 * builds we target.
 */
function ctlOk(mod: OpusModule, name: string, code: number): void {
    if (code !== 0) throw opusError(mod, code, `opus_*_ctl ${name}`);
}
